import { UnscheduledEvent } from '../../types';
import { FlightAutoPopulate } from '../../services/flightService';
import { epochSecondsToZulu } from './dateTimeUtils';

type UpdateField = (field: keyof UnscheduledEvent, value: string | number | boolean) => void;

/**
 * Normal mapping: the previous flight fills Previous, and the next flight fills
 * Chargeable + Next Scheduled (flight number + STD) and Station (its origin).
 */
export function applyNormalFlights(result: FlightAutoPopulate, updateField: UpdateField): void {
  if (result.previousFlight) {
    updateField('inboundFlightNumber', result.previousFlight.flightNumber || '');
    const previousStd = epochSecondsToZulu(result.previousFlight.scheduledDepartureTime);
    if (previousStd) updateField('previousFlightScheduledDepartureTimeZulu', previousStd);
  }

  const flightNumber: string = String(result.nextFlight?.flightNumber || '').trim();
  const std: string = epochSecondsToZulu(result.nextFlight?.scheduledDepartureTime);
  const hasCompleteNextFlight: boolean = Boolean(flightNumber && std);
  updateField('nextScheduledFlightNumber', hasCompleteNextFlight ? flightNumber : '');
  updateField('nextScheduledFlightTimeZulu', hasCompleteNextFlight ? std : '');

  if (result.nextFlight) {
    updateField('outboundFlightNumber', flightNumber);
    if (result.nextFlight.origin) updateField('station', result.nextFlight.origin);
    if (std) {
      // Freeze the Chargeable flight's STD separately so the Audit popup keeps
      // showing it even after the live Next Scheduled refresh moves on.
      updateField('chargeableFlightScheduledDepartureTimeZulu', std);
    }
  }
}

/**
 * RTB/ATO/ATB mapping: the aircraft attempted its previous flight and returned,
 * so that flight (P) becomes the Chargeable + Next Scheduled flight (and sets
 * Station to its origin), and the flight before it (P-1) becomes the Previous
 * flight.
 */
export function applyIropFlights(result: FlightAutoPopulate, updateField: UpdateField): void {
  const chargeable = result.previousFlight; // P — the flight it returned on
  const prior = result.previousPriorFlight; // P-1 — the flight before that

  if (prior) {
    updateField('inboundFlightNumber', prior.flightNumber || '');
    const previousStd = epochSecondsToZulu(prior.scheduledDepartureTime);
    if (previousStd) updateField('previousFlightScheduledDepartureTimeZulu', previousStd);
  }

  const flightNumber: string = String(chargeable?.flightNumber || '').trim();
  const std: string = epochSecondsToZulu(chargeable?.scheduledDepartureTime);
  const hasCompleteNextFlight: boolean = Boolean(flightNumber && std);
  updateField('nextScheduledFlightNumber', hasCompleteNextFlight ? flightNumber : '');
  updateField('nextScheduledFlightTimeZulu', hasCompleteNextFlight ? std : '');

  if (chargeable) {
    updateField('outboundFlightNumber', flightNumber);
    if (chargeable.origin) updateField('station', chargeable.origin);
    if (std) {
      // Freeze the Chargeable flight's STD separately so the Audit popup keeps
      // showing it even after the live Next Scheduled refresh moves on.
      updateField('chargeableFlightScheduledDepartureTimeZulu', std);
    }
  }
}
